import { create } from 'zustand';
import { AuthState, User, SubscriptionStatus } from '../types';
import authService, { RegisterData } from '../services/authService';
import stripeService from '../services/stripeService';
import { useClientStore } from './useClientStore';
import { useQuoteStore } from './useQuoteStore';
import { useFactureStore } from './useFactureStore';
import { extractErrorMessage } from '../utils/errorUtils';

const STORAGE_KEY = 'momo_auth';

interface AuthStore extends AuthState {
  login: (username: string, password: string) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => Promise<void>;
  loadFromStorage: () => void;
  updateUser: (user: Partial<User>) => Promise<void>;
  loadSubscriptionStatus: () => Promise<void>;
  refreshSubscriptionStatus: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  subscriptionStatus: SubscriptionStatus | null;
}

// Fonction pour sauvegarder dans localStorage
const saveToStorage = (state: AuthState) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

// Fonction pour charger depuis localStorage
const loadFromStorage = (): AuthState => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Erreur lors du chargement de l\'auth', e);
    }
  }
  return {
    isAuthenticated: false,
    user: null,
    token: null,
  };
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  // État initial
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: true, // true au démarrage pour charger depuis le storage
  error: null,
  subscriptionStatus: null,

  // Action de login avec API
  login: async (username: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authService.login({ username, password });
      console.log('🔐 Réponse login:', response);

      // Si la réponse contient directement un objet user, on l'utilise
      let user = response.user;

      // Sinon, on récupère les infos utilisateur via l'API /me
      if (!user) {
        console.log('🔐 Pas de user dans la réponse, appel de /me...');

        // Sauvegarder temporairement le token pour que l'intercepteur puisse l'utiliser
        const tempAuthState: AuthState = {
          isAuthenticated: true,
          user: null,
          token: response.token,
        };
        saveToStorage(tempAuthState);

        try {
          user = await authService.getCurrentUser();
          console.log('🔐 User récupéré depuis /me:', user);
        } catch (meError) {
          console.error('🔐 Erreur lors de la récupération du user:', meError);
          // Si on ne peut pas récupérer le user, on crée un user minimal à partir du username
          user = {
            id: '1',
            nom: username,
            email: username,
            entreprise: 'Sersal',
          };
          console.log('🔐 User minimal créé:', user);
        }
      }

      const authState: AuthState = {
        isAuthenticated: true,
        user: user,
        token: response.token,
      };

      set({ ...authState, isLoading: false });
      saveToStorage(authState);

      // Charger le statut d'abonnement après login réussi
      get().loadSubscriptionStatus();

      return true;
    } catch (error: any) {
      console.log("log de verification ", error);
      const errorMessage = extractErrorMessage(error, 'Erreur de connexion');
      set({
        isLoading: false,
        error: errorMessage,
        isAuthenticated: false,
        user: null,
        token: null,
      });
      return false;
    }
  },

  // Action d'inscription avec API
  register: async (data: RegisterData) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authService.register(data);
      console.log('🔐 Réponse inscription:', response);

      // Si la réponse contient directement un objet user, on l'utilise
      let user = response.user;

      // Sinon, on récupère les infos utilisateur via l'API /me
      if (!user) {
        console.log('🔐 Pas de user dans la réponse, appel de /me...');

        // Sauvegarder temporairement le token pour que l'intercepteur puisse l'utiliser
        const tempAuthState: AuthState = {
          isAuthenticated: true,
          user: null,
          token: response.token,
        };
        saveToStorage(tempAuthState);

        try {
          user = await authService.getCurrentUser();
          console.log('🔐 User récupéré depuis /me:', user);
        } catch (meError) {
          console.error('🔐 Erreur lors de la récupération du user:', meError);
          // Créer un user minimal à partir des données d'inscription
          user = {
            id: '1',
            nom: data.nom,
            email: data.email,
            entreprise: data.entreprise,
            telephone: data.telephone,
            adresse: data.adresse,
            siret: data.siret,
          };
          console.log('🔐 User minimal créé:', user);
        }
      }

      const authState: AuthState = {
        isAuthenticated: true,
        user: user,
        token: response.token,
      };

      set({ ...authState, isLoading: false });
      saveToStorage(authState);

      // Charger le statut d'abonnement après inscription réussie
      get().loadSubscriptionStatus();

      return true;
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de l\'inscription');
      set({
        isLoading: false,
        error: errorMessage,
        isAuthenticated: false,
        user: null,
        token: null,
      });
      throw new Error(errorMessage);
    }
  },

  // Action de logout
  logout: async () => {
    // Récupérer l'ID utilisateur avant de nettoyer
    const currentUserId = get().user?.id;

    try {
      await authService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }

    // Vider tous les stores
    console.log('🔐 Nettoyage des stores lors du logout...');
    useClientStore.getState().clearClients();
    useQuoteStore.getState().clearDevis();
    useFactureStore.getState().clearFactures();

    // Nettoyer le cache du dashboard
    try {
      const { useDashboardStore } = await import('./useDashboardStore');
      useDashboardStore.getState().invalidateCache();
      console.log('🔐 Cache du dashboard invalidé');
    } catch (error) {
      console.error('Erreur lors de l\'invalidation du cache dashboard:', error);
    }

    const authState: AuthState = {
      isAuthenticated: false,
      user: null,
      token: null,
    };
    set({ ...authState, isLoading: false, error: null, subscriptionStatus: null });

    // Nettoyer tout le localStorage
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('dashboard-cache');
    console.log('🔐 Cache localStorage nettoyé');
  },

  // Charger l'état depuis le localStorage au démarrage
  loadFromStorage: async () => {
    const stored = loadFromStorage();
    set({ ...stored, isLoading: true, error: null });

    // Si l'utilisateur est authentifié, valider le token en faisant une requête
    if (stored.isAuthenticated && stored.token) {
      try {
        // Essayer de récupérer l'utilisateur actuel pour valider le token
        const user = await authService.getCurrentUser();
        console.log('🔐 Token validé, utilisateur chargé:', user);

        // Mettre à jour l'utilisateur avec les données à jour
        const authState: AuthState = {
          isAuthenticated: true,
          user: user,
          token: stored.token,
        };
        set({ ...authState, isLoading: false });
        saveToStorage(authState);

        // Charger le statut d'abonnement
        get().loadSubscriptionStatus();
      } catch (error) {
        // Si le token est invalide/expiré, l'intercepteur gérera la redirection
        // On nettoie juste l'état local ici
        console.error('🔐 Token invalide ou expiré lors du chargement', error);
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false,
          error: 'Session expirée',
        });
        localStorage.removeItem(STORAGE_KEY);
      }
    } else {
      set({ isLoading: false });
    }
  },

  // Mettre à jour le profil utilisateur
  updateUser: async (userData: Partial<User>) => {
    set({ isLoading: true, error: null });

    try {
      const updatedUser = await authService.updateProfile(userData);
      const currentState = get();

      const authState: AuthState = {
        ...currentState,
        user: updatedUser,
      };

      set({ ...authState, isLoading: false });
      saveToStorage(authState);
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la mise à jour');
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  // Charger le statut d'abonnement de l'utilisateur
  loadSubscriptionStatus: async () => {
    try {
      const status = await stripeService.getSubscriptionStatus();
      console.log('💳 Statut abonnement chargé:', status);

      set({ subscriptionStatus: status });

      // Mettre à jour les champs du user si nécessaire
      const currentUser = get().user;
      if (currentUser) {
        const updatedUser: User = {
          ...currentUser,
          hasActiveAccess: status.hasActiveAccess,
          subscriptionActive: status.subscriptionActive,
          subscriptionEndDate: status.subscriptionEndDate || undefined,
          trialEndDate: status.trialEndDate || undefined,
          stripeCustomerId: status.stripeCustomerId || undefined,
        };

        const authState: AuthState = {
          isAuthenticated: get().isAuthenticated,
          user: updatedUser,
          token: get().token,
        };

        set({ user: updatedUser });
        saveToStorage(authState);
      }
    } catch (error: any) {
      console.error('💳 Erreur lors du chargement du statut d\'abonnement:', error);
      // En cas d'erreur, on ne bloque pas l'application
      // L'utilisateur peut toujours utiliser l'app en mode lecture seule
    }
  },

  // Rafraîchir le statut d'abonnement (utilisé après paiement Stripe)
  refreshSubscriptionStatus: async () => {
    console.log('💳 Rafraîchissement du statut d\'abonnement...');
    await get().loadSubscriptionStatus();
  },
}));